interface ImageInfo {
  width: number;
  height: number;
}

function getImage(url: string, download?: boolean) {
  return new Promise<ImageInfo>((resolve, reject) => {
    const img = new Image();

    img.src = url;

    if (download) {
      // 会导致图片没有缓存, 只有需要下载的场景才加这个属性
      img.crossOrigin = 'anonymous';
    }

    const cleanup = () => {
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onError);
      img.src = '';
    };

    const onLoad = () => {
      const width = img.width;
      const height = img.height;
      cleanup();
      resolve({
        width,
        height,
      });
    };

    const onError = (err: any) => {
      cleanup();
      reject(err);
    };

    img.addEventListener('load', onLoad);
    img.addEventListener('error', onError);
  });
}

export function getImageBlob(url: string, download?: boolean) {
  return new Promise<Blob>((resolve, reject) => {
    const img = new Image();
    img.src = url;
    if (download) {
      // 会导致图片没有缓存, 只有需要下载的场景才加这个属性
      img.crossOrigin = 'anonymous';
    }

    const cleanup = () => {
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onError);
      img.src = '';
    };

    const onLoad = () => {
      const width = img.width;
      const height = img.height;

      const canvas = document.createElement('canvas');

      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');

      if (!context) {
        cleanup();
        canvas.remove();
        reject(new Error('Failed to get canvas context'));
        return;
      }

      context.drawImage(img, 0, 0, width, height);

      canvas.toBlob((blob) => {
        cleanup();
        canvas.remove();
        context.clearRect(0, 0, width, height);

        if (blob) {
          resolve(blob);
        } else {
          reject(blob);
        }
      }, 'image/png');
    };

    const onError = (err: any) => {
      cleanup();
      reject(err);
    };

    img.addEventListener('load', onLoad);
    img.addEventListener('error', onError);
  });
}

export async function getResourceBlobFromURL(url: string) {
  const response = await fetch(url);
  const blob = await response.blob();
  return blob;
}

export async function resourceURLToFile(url: string, name?: string, fileType?: string) {
  const blob = await getResourceBlobFromURL(url);
  const filename = name?.split('.')?.[0] || Date.now().toString();
  const type = fileType || blob.type;
  return new File([blob], `${filename}.${type.split('/').pop()}`, { type });
}

export function toBase64(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(blob);

    const cleanup = () => {
      reader.removeEventListener('load', onLoad);
      reader.removeEventListener('error', onError);
      reader.abort();
    };

    const onLoad = () => {
      cleanup();
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Result type is not string.'));
      }
    };
    const onError = (err: any) => {
      cleanup();
      reject(err);
    };

    reader.addEventListener('load', onLoad);
    reader.addEventListener('error', onError);
  });
}

export function getImageFromBlob(blob: Blob) {
  return toBase64(blob).then(getImage);
}

export function fileConvert(file: File, type: 'image/png' | 'image/jpeg' = 'image/jpeg') {
  const ext = type === 'image/jpeg' ? 'jpg' : 'png';

  return new Promise<File>((resolve, reject) => {
    const reader = new FileReader();

    const cleanup = () => {
      reader.removeEventListener('load', onLoad);
      reader.removeEventListener('error', onError);
      reader.abort();
    };

    const onLoad = (e: ProgressEvent<FileReader>) => {
      cleanup();

      if (!e.target?.result) {
        reject(new Error('File result is null'));
        return;
      }

      const img = new Image();

      const cleanupImg = () => {
        img.removeEventListener('load', onImgLoad);
        img.removeEventListener('error', onImgError);
        img.src = '';
      };

      const onImgLoad = () => {
        const width = img.width;
        const height = img.height;

        const canvas = document.createElement('canvas');

        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext('2d');

        if (!context) {
          cleanupImg();
          canvas.remove();
          reject(new Error('Failed to get canvas context'));
          return;
        }

        context.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          cleanupImg();
          canvas.remove();
          context.clearRect(0, 0, width, height);

          if (blob) {
            const lastIndex = file.name.lastIndexOf('.');
            const filename = lastIndex > -1 ? file.name.slice(0, lastIndex) : Date.now().toString();
            const newFile = new File([blob], `${filename}.${ext}`, {
              type,
              lastModified: file.lastModified,
            });
            resolve(newFile);
          } else {
            reject(new Error('canvas toBlob failed'));
          }
        }, type);
      };

      const onImgError = (err: any) => {
        cleanupImg();
        reject(err);
      };

      img.addEventListener('load', onImgLoad);
      img.addEventListener('error', onImgError);
      img.src = e.target.result as string;
    };

    const onError = () => {
      cleanup();
      reject(new Error('FileReader failed'));
    };

    reader.addEventListener('load', onLoad);
    reader.addEventListener('error', onError);
    reader.readAsDataURL(file);
  });
}

export function getFileImageInfo(file: File) {
  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function (e) {
      const img = new Image();

      img.onload = function () {
        resolve({
          width: img.width,
          height: img.height,
        });
      };

      img.onerror = function () {
        reject(new Error('图片加载失败'));
      };

      img.src = e?.target?.result as string;
    };

    reader.onerror = function () {
      reject(new Error('文件读取失败'));
    };

    reader.readAsDataURL(file);
  });
}

export default getImage;
